import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Stack, Typography, Checkbox, FormControlLabel, FormGroup,
  ToggleButtonGroup, ToggleButton, Select, MenuItem,
  FormControl, InputLabel, Alert, CircularProgress, Chip, Box,
  Slider, Divider,
} from '@mui/material';
import { ShieldCheck, QrCode, Smartphone } from 'lucide-react';
import { api } from '../../lib/api';
import type {
  GlobalPatientSearchResponse, ShareableSection, ConsentPurpose,
  ApprovalChannel, ConsentRequestResponse,
} from '../../types';

const ALL_SECTIONS: Array<{ key: ShareableSection; label: string; description: string }> = [
  { key: 'ALLERGIES',     label: 'Allergies',          description: 'Known allergens and reactions' },
  { key: 'MEDICATIONS',   label: 'Medications',         description: 'Current and past medications' },
  { key: 'PRESCRIPTIONS', label: 'Prescriptions',       description: 'Prescription summaries (no raw files)' },
  { key: 'VITALS',        label: 'Vitals',              description: 'BP, pulse, SpO2, temperature, weight' },
  { key: 'LABS',          label: 'Lab Summaries',       description: 'Test names and results (no raw documents)' },
  { key: 'VISITS',        label: 'Visit History',       description: 'Visit dates and chief complaints' },
  { key: 'DIAGNOSIS',     label: 'Diagnosis History',   description: 'Recorded diagnoses' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  patient: GlobalPatientSearchResponse | null;
  sourceClinicId: string;
  onConsentCreated: (response: ConsentRequestResponse) => void;
}

export function ConsentRequestDialog({ open, onClose, patient, sourceClinicId, onConsentCreated }: Props) {
  const [sections, setSections]         = useState<ShareableSection[]>(['ALLERGIES', 'MEDICATIONS', 'PRESCRIPTIONS', 'VITALS']);
  const [purpose, setPurpose]           = useState<ConsentPurpose>('TREATMENT');
  const [channel, setChannel]           = useState<ApprovalChannel>('QR');
  const [validDays, setValidDays]       = useState(30);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);

  const toggleSection = (key: ShareableSection) => {
    setSections(prev => prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]);
  };

  const handleSubmit = async () => {
    if (!patient?.globalPatientId) return;
    if (sections.length === 0) { setError('Select at least one data section to share'); return; }
    setLoading(true); setError(null);

    const res = await api.consent.requestConsent({
      globalPatientId: patient.globalPatientId,
      sourceClinicId,
      allowedSections: sections,
      purpose,
      approvalChannel: channel,
      validDays,
    });

    setLoading(false);
    if (res.error) { setError(res.error); return; }
    onConsentCreated(res.data!);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <ShieldCheck size={20} />
          <span>Request Patient Record Sharing Consent</span>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {patient && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Requesting consent for <strong>{patient.maskedName}</strong> ({patient.maskedMobile})
          </Alert>
        )}

        {/* Approval Channel */}
        <Typography variant="subtitle2" gutterBottom>Consent Method</Typography>
        <ToggleButtonGroup value={channel} exclusive onChange={(_, v) => v && setChannel(v)} fullWidth size="small" sx={{ mb: 2 }}>
          <ToggleButton value="QR">
            <QrCode size={14} style={{ marginRight: 6 }} /> QR Code (Recommended)
          </ToggleButton>
          <ToggleButton value="OTP">
            <Smartphone size={14} style={{ marginRight: 6 }} /> SMS OTP
          </ToggleButton>
        </ToggleButtonGroup>

        {channel === 'QR' && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<QrCode size={14} />}>
            A QR code will be displayed — patient scans it on their phone and confirms sharing.
          </Alert>
        )}
        {channel === 'OTP' && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<Smartphone size={14} />}>
            An OTP will be sent to the patient's registered mobile. Patient reads OTP aloud.
          </Alert>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Purpose */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Purpose of Sharing</InputLabel>
          <Select value={purpose} label="Purpose of Sharing" onChange={e => setPurpose(e.target.value as ConsentPurpose)}>
            <MenuItem value="TREATMENT">Treatment at this clinic</MenuItem>
            <MenuItem value="FOLLOW_UP">Follow-up care</MenuItem>
            <MenuItem value="SECOND_OPINION">Second opinion</MenuItem>
          </Select>
        </FormControl>

        {/* Valid Days */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Valid for: <strong>{validDays} days</strong>
          </Typography>
          <Slider value={validDays} onChange={(_, v) => setValidDays(v as number)}
            min={1} max={90} step={1} marks={[{value:7,label:'7d'},{value:30,label:'30d'},{value:90,label:'90d'}]}
          />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Sections */}
        <Typography variant="subtitle2" gutterBottom>Data to Share (select minimum required)</Typography>
        <FormGroup>
          {ALL_SECTIONS.map(s => (
            <FormControlLabel
              key={s.key}
              control={<Checkbox checked={sections.includes(s.key)} onChange={() => toggleSection(s.key)} size="small" />}
              label={
                <Box>
                  <Typography variant="body2">{s.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.description}</Typography>
                </Box>
              }
            />
          ))}
        </FormGroup>

        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">Selected: </Typography>
          {sections.map(s => <Chip key={s} label={s} size="small" sx={{ mr: 0.5 }} />)}
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}
          startIcon={loading ? <CircularProgress size={14} /> : <ShieldCheck size={14} />}>
          {channel === 'QR' ? 'Generate QR Code' : 'Send OTP'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
